import axios from "axios";
import * as config from "./../configuration/index";
import {
  getElementCache,
  getElementDepthToParent,
  getOrCreateEntry,
} from "./ElementCache";
import { extractLatestBatchList } from "./QueryBatchList";
import QueryBuilder from "./QueryBuilder";
import QueryComposer from "./QueryComposer";
import { QueryRecursionType } from "./QueryRecursionType";
import { infoCacheLogBlue } from "./log";

const startBatcherService = (frequencyMilliseconds: number) => {
  /**
   * Batcher to solve queries
   */
  setInterval(async () => {
    const timeNow = Date.now();
    // ask new queries, filter away the ones which do not have any elements
    const newBatchList = (extractLatestBatchList() as QueryBuilder[]).filter(
      (q) => q.elementIds.length > 0
    );
    if (newBatchList.length > 0) {
      infoCacheLogBlue("Sending a batch of ", newBatchList.length, " queries");
      console.log(
        `activeElementCache ${Object.keys(getElementCache()).length} items `,
        getElementCache()
      );
      const queriesThatNeedsToBeSent = QueryComposer(newBatchList);
      /**
       * Here is important point for the recursive cache: we can ask any number of depth in the query, but
       * it is possible that there are no existing children at that level. In that case the cache algorithm would
       * fail to reach the bottom of the query and fail, unless we mark the .children or .childrenOfType as
       * "resolved" by assigning an empty array to them just in case there will never be any more children
       */
      const validateRecursiveChildren = (
        query: QueryBuilder,
        row: any,
        recursionType: QueryRecursionType
      ) => {
        if (row.id) {
          const cacheEntry = getOrCreateEntry(row.id);
          if (cacheEntry.parentId) {
            query.elementIds.forEach((elementId) => {
              const depth = getElementDepthToParent(elementId, row.id);
              if (depth !== null && recursionType.depth) {
                /**
                 *  Make sure the children are set in case we are not yet reached the max depth of this query
                 */
                if (depth < recursionType.depth) {
                  if (recursionType.hasFilter) {
                    if (!cacheEntry.childrenOfType) {
                      cacheEntry.childrenOfType = {};
                    }
                    if (
                      typeof recursionType.filterType === "string" &&
                      !cacheEntry.childrenOfType[recursionType.filterType]
                    ) {
                      cacheEntry.childrenOfType[recursionType.filterType] = {};
                    }
                  } else if (!cacheEntry.children) {
                    cacheEntry.children = {};
                  }
                }
              }
            });
          }
        }
      };
      const storeQueryResultToCache = (query: QueryBuilder, row: any) => {
        if (row.id) {
          const cacheEntry = getOrCreateEntry(row.id);
          const statusPeriods = query.getPeriodStatus(row);
          const draftSeries = query.getDraftSeries(row);
          query.getAttributes().forEach((attr) => {
            const value = row[attr.getQueryName()];
            const entry = attr.createCacheEntry(value, timeNow);
            if (draftSeries.length > 0) {
              if (attr.options?.period) {
                const status = statusPeriods[attr.options.period];
                if (config.System.draftValuesHandling.isDraftStatus(status)) {
                  const isDrafSeries = draftSeries.find(
                    (ser) =>
                      ser.toLocaleLowerCase() ===
                      attr.getSeriesName().toLocaleLowerCase()
                  );
                  if (isDrafSeries) {
                    entry.isDraft = true;
                  }
                }
              }
            }
            cacheEntry.attributes[attr.signature()] = entry;
            if (attr.getName() === "parent_element_id") {
              cacheEntry.parentId = value;
              const filteredType = query.getRecursionType();
              /**
               * without filter we can fill both children and childrenOfType
               */
              if (filteredType.isRecursive && !filteredType.hasFilter) {
                const entry = getOrCreateEntry(value);
                entry.children = entry.children || {};
                entry.children[row.id] = row.id;
                if (row.typename) {
                  if (!entry.childrenOfType) {
                    entry.childrenOfType = {};
                  }
                  if (!entry.childrenOfType[row.typename]) {
                    entry.childrenOfType[row.typename] = {};
                  }
                  entry.childrenOfType[row.typename][row.id] = row.id;
                }
              }
              /**
               * if filteredType then only childrenOfType can be filled
               */
              if (
                filteredType.isRecursive &&
                filteredType.hasFilter &&
                typeof filteredType.filterType === "string"
              ) {
                const entry = getOrCreateEntry(value);
                if (!entry.childrenOfType) {
                  entry.childrenOfType = {};
                }
                if (!entry.childrenOfType[filteredType.filterType]) {
                  entry.childrenOfType[filteredType.filterType] = {};
                }
                entry.childrenOfType[filteredType.filterType][row.id] = row.id;
              }
            }
          });
        }
      };
      queriesThatNeedsToBeSent.forEach((q) => {
        if (config.System.query.logQueries) {
          console.log("Sending request with attributes");
          q.getAttributes().forEach((attr) => {
            console.log(" - ", attr.toString(), "sig: ", attr.signature());
          });
        }
        axios
          .post<{
            data: any[];
          }>("meaquery", {
            query: q.toMeaQuery(),
            attributes: q.toMeaAttributes(),
            sortby: q._sortBy,
          })
          .then((result) => {
            if (q.replacesQueries) {
              console.log("Relace queries data ", q, result);
              q.replacesQueries.forEach((subQuery) => {
                const recursionType = subQuery.getRecursionType();
                subQuery.resolve(
                  result.data.data
                    .filter((row) => {
                      return subQuery.elementIds.includes(row.id);
                    })
                    .map((row) => {
                      subQuery.markDraftResults(row);
                      if (config.System.query.enableObjectCache) {
                        storeQueryResultToCache(subQuery, row);
                      }
                      // storeQueryResultToCache(subQuery, row);
                      return row;
                    })
                    .map((row) => {
                      /**
                       * The validation of the recursive hierarchy is postponed until all the rows are processed,
                       * because we do not know the order in which the rows arrive
                       */
                      validateRecursiveChildren(subQuery, row, recursionType);
                      return row;
                    })
                    .map((row) => subQuery.transformResultObject(row))
                );
              });
            } else {
              const recursionType = q.getRecursionType();
              q.resolve(
                result.data.data
                  .map((row) => {
                    q.markDraftResults(row);
                    if (config.System.query.enableObjectCache) {
                      storeQueryResultToCache(q, row);
                    }
                    return row;
                  })
                  .map((row) => {
                    /**
                     * The validation of the recursive hierarchy is postponed until all the rows are processed,
                     * because we do not know the order in which the rows arrive
                     */
                    validateRecursiveChildren(q, row, recursionType);
                    return row;
                  })
                  .map((row) => q.transformResultObject(row))
              );
            }
          })
          .catch((err) => {
            q.reject(err);
            if (q.replacesQueries) {
              q.replacesQueries.forEach((q) => q.reject(err));
            }
          });
      });
    }
  }, frequencyMilliseconds);
};
export default startBatcherService;
